package com.fantasytenis.shared.core.utils

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import com.fantasytenis.shared.domain.auth.entity.UserEntity
import com.fantasytenis.shared.domain.wallet.entity.WalletEntity
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity
import com.fantasytenis.shared.domain.live.entity.ScoreEvent
import com.fantasytenis.shared.domain.live.entity.PreviousMatchStats
import com.fantasytenis.shared.domain.engagement.entity.EngagementEntity
import com.fantasytenis.shared.domain.engagement.entity.AchievementEntity
import com.fantasytenis.shared.domain.notifications.entity.NotificationEntity
import com.fantasytenis.shared.core.realtime.LiveScorePayload

/**
 * Type alias for in-app notifications.
 * Uses NotificationEntity from the domain layer.
 */
typealias AppNotification = NotificationEntity

/**
 * SharedData Singleton
 *
 * Global reactive state management using MutableStateFlow.
 * This is the central state store for the application.
 *
 * Usage:
 * ```kotlin
 * // Reading state
 * SharedData.currentUser.value
 *
 * // Observing in Compose
 * val user by SharedData.currentUser.collectAsState()
 * ```
 *
 * Rules:
 * - Modify SharedData only from use cases or ViewModels after API calls
 * - Never modify directly from UI components
 * - Use StateFlow for read-only access where possible
 */
object SharedData {

    // ==========================================
    // Authentication State
    // ==========================================

    /** Whether the user is currently logged in */
    val loggedIn = MutableStateFlow(false)

    /** Current access token for API calls */
    val accessToken = MutableStateFlow<String?>(null)

    /** Refresh token for obtaining new access tokens */
    val refreshToken = MutableStateFlow<String?>(null)

    /** Current authenticated user */
    val currentUser = MutableStateFlow<UserEntity?>(null)

    /** Whether the user profile has been loaded */
    val isProfileLoaded = MutableStateFlow(false)

    // ==========================================
    // Wallet State
    // ==========================================

    /** User's wallet with balance information */
    val wallet = MutableStateFlow<WalletEntity?>(null)

    // ==========================================
    // Live Scoring State
    // ==========================================

    /** Map of match ID to live score data for active matches */
    val liveMatches = MutableStateFlow<Map<String, LiveScoreEntity>>(emptyMap())

    /** Whether connected to the realtime WebSocket service */
    val connectedToRealtime = MutableStateFlow(false)

    // ==========================================
    // Game Day Live State (Phase 0)
    // ==========================================

    /**
     * Previous match statistics for delta computation.
     * Key: "matchId:playerId"
     * Used to detect changes between WebSocket updates and generate ScoreEvents.
     */
    val previousMatchStats = MutableStateFlow<Map<String, PreviousMatchStats>>(emptyMap())

    /**
     * Live score events for the event feed.
     * Most recent events first, capped at 50 events.
     */
    val scoreEvents = MutableStateFlow<List<ScoreEvent>>(emptyList())

    /**
     * Raw LiveScorePayload data for detailed stat access.
     * Key: matchId
     */
    val liveScorePayloads = MutableStateFlow<Map<String, LiveScorePayload>>(emptyMap())

    // ==========================================
    // Contest State
    // ==========================================

    /** User's active contest entries */
    val myActiveEntries = MutableStateFlow<List<EntryEntity>>(emptyList())

    // ==========================================
    // Engagement State (Phase 1)
    // ==========================================

    /** User's engagement data (streaks, stats, achievements) */
    val engagement = MutableStateFlow<EngagementEntity?>(null)

    /** All available achievement definitions */
    val achievements = MutableStateFlow<List<AchievementEntity>>(emptyList())

    // ==========================================
    // Notifications State
    // ==========================================

    /** In-app notifications for the user */
    val notifications = MutableStateFlow<List<AppNotification>>(emptyList())

    /** Count of unread notifications (for badge display) */
    val unreadNotificationCount = MutableStateFlow(0)

    // ==========================================
    // App Configuration
    // ==========================================

    /** App version string */
    val appVersion = MutableStateFlow("Unknown")

    /** Unique device identifier */
    val deviceId = MutableStateFlow("Unknown")

    // ==========================================
    // Admin / Platform Configuration
    // ==========================================

    /** Whether paid games should be shown to users */
    val showPaidGames = MutableStateFlow(false)

    // ==========================================
    // Utility Functions
    // ==========================================

    /**
     * Reset all state to initial values.
     * Called on logout.
     */
    fun reset() {
        loggedIn.value = false
        accessToken.value = null
        refreshToken.value = null
        currentUser.value = null
        isProfileLoaded.value = false
        wallet.value = null
        liveMatches.value = emptyMap()
        connectedToRealtime.value = false
        myActiveEntries.value = emptyList()
        // Game Day Live state
        previousMatchStats.value = emptyMap()
        scoreEvents.value = emptyList()
        liveScorePayloads.value = emptyMap()
        // Engagement state
        engagement.value = null
        achievements.value = emptyList()
        // Notifications state
        notifications.value = emptyList()
        unreadNotificationCount.value = 0
    }

    /**
     * Update live match score.
     */
    fun updateLiveScore(matchId: String, score: LiveScoreEntity) {
        val current = liveMatches.value.toMutableMap()
        current[matchId] = score
        liveMatches.value = current
    }

    /**
     * Remove a match from live scores (when match ends).
     */
    fun removeLiveMatch(matchId: String) {
        val current = liveMatches.value.toMutableMap()
        current.remove(matchId)
        liveMatches.value = current
    }
}
